import { ImplementationGroup } from '@constants/entities/project';
import { IQnousRef } from '@store/services/qnous-ref/types';
import { compareCisIds, getEntryTier } from '@utils/implementationGroup';

/**
 * One CIS-18 reference as the filter panel lists it: a Control or a
 * Safeguard, with the title being the catalogue's `"8.5 Title"` name less
 * its own id, the `"8.5 Title"` row label built back from the two, the id
 * of the Control a Safeguard belongs to (a Control's own id for a Control)
 * and the entry tier the drawer badges show, `null` for a Control.
 */
export type CisReference = {
  id: string;
  title: string;
  label: string;
  isControl: boolean;
  controlId: string;
  tier: ImplementationGroup | null;
};

/**
 * A Control with its Safeguards in catalogue order — one Controls row and
 * the list its side panel shows.
 */
export type CisControl = CisReference & { safeguards: CisReference[] };

/**
 * The CIS-18 catalogue as the filter reads it: every visible reference in
 * catalogue order, the Controls with their Safeguards nested, and an id
 * lookup. `references.length` is the "X references" of the search hint.
 */
export type CisCatalogue = {
  references: CisReference[];
  controls: CisControl[];
  byId: Record<string, CisReference>;
};

export const EMPTY_CIS_CATALOGUE: CisCatalogue = {
  references: [],
  controls: [],
  byId: {},
};

/**
 * The title of a catalogue entry whose `name` arrives with its own id in
 * front, as the CIS workbook writes it (`"8.5 Title"`, or `"8.5: Title"`):
 * whatever follows that id and any colon, trimmed. The id must end there,
 * so `"1.10 Title"` is not cut by the id `1.1`. A name that does not start
 * with the id is its own title, as is one that is nothing but the id, so a
 * row is never left blank.
 */
export const cisTitle = (name: string, id: string): string => {
  const escapedId = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const ownIdPrefix = new RegExp(`^\\s*${escapedId}(?![\\d.])\\s*:?\\s*`);
  const title = name.replace(ownIdPrefix, '');

  return title.trim() || name.trim();
};

const toReference = ({
  id,
  name,
  level,
  implementation_group: membership,
}: IQnousRef): CisReference => {
  const isControl = level === 1;
  const title = cisTitle(name, id);

  return {
    id,
    title,
    label: `${id} ${title}`,
    isControl,
    controlId: isControl ? id : id.split('.')[0],
    tier: isControl ? null : getEntryTier(membership),
  };
};

/**
 * The catalogue the filter panel reads, built from the `cis` list of the
 * reference payload. Hidden entries are left out, as the drawer's dropdown
 * leaves them out; what remains is sorted by `compareCisIds`, which places
 * a Control immediately before its own Safeguards, so the list is in
 * catalogue order. A Control is a level-1 entry and a Safeguard a level-2
 * one, the two depths the catalogue has; a Safeguard's Control is the part
 * of its id before the first dot, since the payload carries no parent
 * field. A Safeguard whose Control is hidden or missing stays in
 * `references` — still searchable, still listed under its tier — but
 * under no Control.
 */
export const buildCisCatalogue = (refs: IQnousRef[]): CisCatalogue => {
  const references = refs
    .filter(({ hidden }) => !hidden)
    .map(toReference)
    .sort((a, b) => compareCisIds(a.id, b.id));

  const controls = references
    .filter(({ isControl }) => isControl)
    .map((control) => ({
      ...control,
      safeguards: references.filter(
        (reference) =>
          !reference.isControl && reference.controlId === control.id,
      ),
    }));

  const byId = Object.fromEntries(
    references.map((reference) => [reference.id, reference]),
  );

  return { references, controls, byId };
};

/**
 * The Safeguards whose entry tier is `tier`, in catalogue order — the IGn
 * pill's list. A Control carries no tier and never appears.
 */
export const safeguardsOfTier = (
  catalogue: CisCatalogue,
  tier: ImplementationGroup,
): CisReference[] =>
  catalogue.references.filter((reference) => reference.tier === tier);

/**
 * The references whose id or title contains `query`, case-insensitively,
 * in catalogue order — the flat result list of AC5.4.1. A blank query
 * matches nothing; the panel shows its hint instead.
 */
export const searchCisReferences = (
  catalogue: CisCatalogue,
  query: string,
): CisReference[] => {
  const needle = query.trim().toLowerCase();

  if (!needle) return [];

  return catalogue.references.filter(
    ({ id, title }) =>
      id.toLowerCase().includes(needle) || title.toLowerCase().includes(needle),
  );
};

/**
 * The ids a Control's side panel offers, in its row order: the Control
 * itself, then its Safeguards.
 */
export const controlValueIds = (control: CisControl): string[] => [
  control.id,
  ...control.safeguards.map(({ id }) => id),
];

/**
 * How many of `ids` are selected — a Control row's badge, where the
 * Control and each Safeguard count once.
 */
export const countSelected = (ids: string[], selected: string[]): number =>
  ids.filter((id) => selected.includes(id)).length;

export type SelectionCoverage = 'none' | 'some' | 'all';

/**
 * How much of a list the selection covers: the Select all checkbox reads
 * checked for `all`, indeterminate for `some`. An empty list is `none`.
 */
export const selectionCoverage = (
  ids: string[],
  selected: string[],
): SelectionCoverage => {
  const count = countSelected(ids, selected);

  if (count === 0) return 'none';

  return count === ids.length ? 'all' : 'some';
};

/**
 * The selection a Select all click produces: with the whole list selected
 * every id of the list is removed; otherwise the ids still missing are
 * appended. Ids outside the list are never touched, so Select all on an
 * IGn pill or a Control's side panel reaches only what that list shows.
 */
export const toggleAll = (selected: string[], ids: string[]): string[] => {
  if (selectionCoverage(ids, selected) === 'all') {
    return selected.filter((id) => !ids.includes(id));
  }

  return [...selected, ...ids.filter((id) => !selected.includes(id))];
};
